import { useCallback, useEffect, useRef, useState } from 'react'
import { get } from '../lib/api'
import { me, signOut, type User } from '../lib/auth'
import { restoreNativePush } from '../lib/nativePush'
import { kitchen, type BuddyChoice } from '../lib/kitchen'
import { activeSessions, bootstrap, listCategories, listRecipes, type Category, type RecipeRow, type Session } from '../lib/savenmake'
import { celebrate, confetti, floatText, sound } from '../lib/fun'
import { momentsBetween, type KitchenSummary } from '../../shared/kitchen'
import type { AnyMoment } from '../components/sm/Game'
import type { Health } from '../App'

/** Account data and achievement comparisons belong to one authenticated session. */
export function useAccountKitchen() {
  const [user, setUser] = useState<User | null>(null)
  const [health, setHealth] = useState<Health | null>(null)
  const [ready, setReady] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const signOutPending = useRef(false)
  const [recipes, setRecipes] = useState<RecipeRow[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [running, setRunning] = useState<Session[]>([])
  const [summary, setSummary] = useState<KitchenSummary | null>(null)
  const [buddy, setBuddy] = useState<BuddyChoice | null>(null)
  const [moments, setMoments] = useState<AnyMoment[]>([])
  const lastSummary = useRef<KitchenSummary | null>(null)
  const epoch = useRef(0)
  const latestReload = useRef(0)
  const account = useRef<string | null>(null)
  const mounted = useRef(true)

  // Invalidate requests BEFORE awaiting auth. Even re-entering the same account
  // starts with a silent baseline and cannot inherit an undismissed popup.
  const clearSession = useCallback(() => {
    epoch.current += 1
    latestReload.current += 1
    account.current = null
    lastSummary.current = null
    setUser(null)
    setRecipes([])
    setCategories([])
    setRunning([])
    setSummary(null)
    setBuddy(null)
    setMoments([])
    return epoch.current
  }, [])

  const refreshUser = useCallback(async () => {
    if (signOutPending.current) return
    const request = clearSession()
    const u = await me()
    if (!mounted.current || request !== epoch.current) return
    account.current = u?.id ?? null
    setUser(u)
  }, [clearSession])

  const logOut = useCallback(async () => {
    if (signOutPending.current) return
    signOutPending.current = true
    setSigningOut(true)
    const request = clearSession()
    try {
      await signOut()
    } finally {
      signOutPending.current = false
      // Hide sign-in until the server has completed sign-out; otherwise its
      // delayed cookie response could invalidate a newly entered account.
      if (mounted.current && request === epoch.current) {
        try { await refreshUser() } finally { setSigningOut(false) }
      }
    }
  }, [clearSession, refreshUser])

  const reload = useCallback(async () => {
    if (!mounted.current || !account.current) return
    const session = epoch.current
    const request = ++latestReload.current
    const [r, c, s, k] = await Promise.all([
      listRecipes().catch(() => []),
      listCategories().catch(() => []),
      activeSessions().catch(() => []),
      kitchen().catch(() => null),
    ])
    if (!mounted.current || session !== epoch.current || request !== latestReload.current) return
    setRecipes(r)
    setCategories(c)
    setRunning(s)
    if (!k) return
    const before = lastSummary.current
    lastSummary.current = k.summary
    setSummary(k.summary)
    setBuddy(k.buddy)
    // The first successful summary hydrates history; only later changes earn UI.
    if (!before) return
    const m = momentsBetween(before, k.summary)
    if (m.xp > 0) floatText(`+${m.xp} XP`, { x: innerWidth / 2, y: innerHeight / 3 })
    const queue: AnyMoment[] = [
      ...(m.levelUp ? [{ kind: 'level' as const, level: m.levelUp }] : []),
      ...m.newBadges.map((key) => ({ kind: 'badge' as const, key })),
    ]
    if (queue.length) {
      sound(m.levelUp ? 'level' : 'badge')
      celebrate()
      setMoments((q) => [...q, ...queue])
    } else if (m.xp >= 40) {
      confetti(50)
    }
  }, [])

  useEffect(() => {
    mounted.current = true
    const request = epoch.current
    void (async () => {
      const [u, h] = await Promise.all([me(), get<Health>('/api/health').catch(() => null)])
      if (!mounted.current) return
      if (request === epoch.current) {
        account.current = u?.id ?? null
        setUser(u)
      }
      setHealth(h)
      setReady(true)
    })()
    return () => {
      mounted.current = false
      epoch.current += 1
      latestReload.current += 1
    }
  }, [])

  useEffect(() => {
    if (!user) return
    let active = true
    const session = epoch.current
    // Starter vocabulary is server-idempotent. A stale bootstrap must not start
    // another account's reload after its session has already ended.
    void bootstrap().catch(() => {}).then(() => {
      if (active && session === epoch.current) return reload()
    })
    const stop = restoreNativePush(user.id)
    return () => { active = false; stop() }
  }, [user, reload])

  const dismissMoment = useCallback(() => setMoments((q) => q.slice(1)), [])
  return { user, health, ready, signingOut, recipes, categories, running, summary, buddy, setBuddy, moments, dismissMoment, refreshUser, logOut, reload }
}
